/**
 * Nederlandse namen voor alles wat als kale databasewaarde op het scherm kan komen.
 *
 * Waarom hier: tot 21-08-2026 stonden er drie halve lijstjes verspreid over de app en
 * had de audit-log er helemaal geen. Wat niet in een lijstje stond, kwam rauw op het
 * scherm: `uitvoering_pijnplek_toevoegen` in plaats van "Pijnplek toegevoegd".
 * Dit bestand is de enige plek waar zo'n waarde zijn Nederlandse naam krijgt.
 *
 * Twee vangnetten houden het compleet:
 *   1. `namen.test.js` leest de backend-broncode en faalt zodra daar een actie bij komt
 *      die hier geen naam heeft.
 *   2. `noemNetjes()` maakt van een onbekende waarde alsnog leesbare tekst, zodat er
 *      nooit een liggend streepje op het scherm belandt — ook niet bij iets nieuws
 *      dat de database zelf verzint.
 */

/**
 * Auditlog-acties.
 *
 * Let op: niet alles komt uit de Python-code. `INSERT`, `UPDATE` en `DELETE` worden door
 * een databasetrigger geschreven (samen ruim 14.000 van de 25.000 regels), en
 * `avg_anonymise` en `followup_submit` komen uit database-functies. Die vind je dus niet
 * door de backend te doorzoeken; ze staan hieronder gemarkeerd.
 */
export const AUDIT_ACTIE_NAMEN = {
  // — Inloggen en account
  login: "Ingelogd",
  logout: "Uitgelogd",
  logout_all: "Overal uitgelogd",
  wachtwoord_vergeten_aangevraagd: "Herstelmail aangevraagd",
  wachtwoord_hersteld: "Wachtwoord hersteld",
  mfa_enroll_start: "Tweestapsverificatie instellen gestart",
  mfa_enroll_voltooid: "Tweestapsverificatie ingeschakeld",
  mfa_challenge_verified: "Tweestapscode goedgekeurd",
  mfa_disabled: "Tweestapsverificatie uitgezet",

  // — Werkgevers
  werkgever_aanmaken: "Werkgever aangemaakt",
  werkgever_bijwerken: "Werkgever bijgewerkt",
  aanmeldcode_genereren: "Aanmeldcode aangemaakt",

  // — Werknemers
  werknemer_aanmaken: "Werknemer aangemaakt",
  werknemer_bijwerken: "Werknemer bijgewerkt",
  werknemer_uitnodiging_verstuurd: "Aanmeldmail verstuurd",
  werknemer_opzeggen: "Werknemer opgezegd",
  werknemer_self_aanmelden: "Werknemer heeft zich zelf aangemeld",
  nulmeting_link_klaargezet: "Vragenlijst-link klaargezet op verzoek van de deelnemer",
  werknemer_status_override: "Status handmatig gewijzigd",
  werknemer_bulk_csv: "Werknemers via CSV ingelezen",
  werknemer_aan_aanmeetdag_toewijzen: "Werknemer aan aanmeetdag toegewezen",

  // — Vragenlijsten
  baseline_submit: "Nulmeting ingevuld",
  followup_submit: "Vervolgmeting ingevuld", // door de database geschreven

  // — Aanmeetdagen
  aanmeetdag_aanmaken: "Aanmeetdag aangemaakt",
  aanmeetdag_bevestigen: "Aanmeetdag bevestigd",
  aanmeetdag_annuleren: "Aanmeetdag geannuleerd",
  tijdslot_aanmaken: "Tijdslot aangemaakt",

  // — De aanmeting zelf
  uitvoering_starten: "Aanmeting gestart",
  uitvoering_voltooien: "Aanmeting afgerond",
  uitvoering_verzet: "Aanmeting verzet",
  uitvoering_no_show: "Niet verschenen",
  uitvoering_adres_controle: "Adres gecontroleerd",
  uitvoering_scan_invoeren: "Scan ingevoerd",
  uitvoering_profiel_invoeren: "Zoolprofiel ingevoerd",
  uitvoering_pijnplek_toevoegen: "Pijnplek toegevoegd",
  pijn_flag_afhandelen: "Pijn-signaal afgehandeld",

  // — Bestellingen
  bestelling_status_update: "Bestelstatus gewijzigd",
  bestelling_herbesteld: "Opnieuw besteld",
  bestelling_csv_export_werkgever: "Bestellijst gedownload (per werkgever)",
  bestelling_csv_export_aanmeetdag: "Bestellijst gedownload (per aanmeetdag)",
  bestelling_csv_import_leverdatums: "Leverdatums ingelezen",
  bestelling_direct: "Direct besteld, zonder aanmeetdag",
  bestelling_stapel_besteld: "Stapel in één keer besteld",
  bestelmail_verstuurd: "Bestelmail naar de leverancier verstuurd",
  bestelling_csv_export_zoolsoort: "Bestellijst gedownload (per zoolsoort)",

  // — Berichten
  notificatie_csv_export: "Berichtenlijst gedownload",
  notificatie_dry_run: "Berichten proefgedraaid",
  notificatie_resend: "Bericht opnieuw verstuurd",
  template_update: "E-mailsjabloon gewijzigd",

  // — Beheer
  gebruiker_aanmaken: "Gebruiker aangemaakt",
  gebruiker_bijwerken: "Gebruiker bijgewerkt",
  instelling_bijwerken: "Instelling gewijzigd",
  ui_preferences_update: "Weergavevoorkeur opgeslagen",
  notitie_aanmaken: "Notitie toegevoegd",
  notitie_bewerken: "Notitie bewerkt",
  notitie_verwijderen: "Notitie verwijderd",

  // — Privacy
  avg_toestemming_vastgelegd: "Toestemming vastgelegd door een beheerder",
  avg_anonymise: "Gegevens geanonimiseerd (AVG)", // door de database geschreven

  // — Schaduwregels van de databasetrigger
  INSERT: "Rij toegevoegd",
  UPDATE: "Rij gewijzigd",
  DELETE: "Rij verwijderd",
};

/** Acties die de database zelf schrijft. Doorzoeken van de backend vindt deze niet. */
export const AUDIT_ACTIES_UIT_DE_DATABASE = ["INSERT", "UPDATE", "DELETE", "avg_anonymise", "followup_submit"];

/**
 * Waar een auditregel over gaat. De waarden staan meervoudig in de database
 * (`werknemers`, niet `werknemer`) — daar is het filter op deze pagina jarenlang
 * op stukgelopen.
 */
export const AUDIT_ENTITEIT_NAMEN = {
  werknemers: "Werknemer",
  werkgevers: "Werkgever",
  aanmeetdagen: "Aanmeetdag",
  aanmeet_tijdsloten: "Tijdslot",
  aanmeting_uitvoering: "Aanmeting",
  bestellingen: "Bestelling",
  pijn_flags: "Pijn-signaal",
  notificaties_log: "Bericht",
  email_templates: "E-mailsjabloon",
  app_instellingen: "Instelling",
  users: "Gebruiker",
  auth: "Inloggen",
};

/**
 * Soorten berichten. Eén lijst voor de e-mailsjablonen en voor de verstuurde berichten:
 * het zijn dezelfde waarden, en twee lijsten lopen gegarandeerd uit de pas.
 */
export const BERICHT_NAMEN = {
  werknemer_welkom: "Welkom (werknemer)",
  werknemer_welkom_baseline: "Welkom met uitnodiging nulmeting",
  werknemer_uitnodiging: "Aanmeldmail",
  werknemer_nulmeting_link: "Vragenlijst-link",
  baseline_reminder: "Herinnering nulmeting",
  week4_uitnodiging: "Uitnodiging zolen-wencontrole (week 4)",
  week4_reminder: "Herinnering zolen-wencontrole (week 4)",
  week12_uitnodiging: "Uitnodiging vervolgmeting (week 12)",
  week12_reminder: "Herinnering vervolgmeting (week 12)",
  wen_evaluatie_week2: "Wen-evaluatie (week 2)",
  werknemer_aanmeetdag_bevestiging: "Bevestiging aanmeetdag",
  levering_bevestiging: "Bevestiging levering zolen",
  werkgever_uitnodiging: "Uitnodiging (werkgever)",
  admin_pijnflag_alert: "Melding aan het team: pijn-signaal",
  admin_no_show_alert: "Melding aan het team: niet verschenen",
  admin_signaal_direct: "Melding aan het team: signaal direct",
  admin_signaal_digest: "Melding aan het team: signaaloverzicht",
};

/** Verzendkanalen. */
export const KANAAL_NAMEN = {
  email: "E-mail",
  sms: "Sms",
  whatsapp: "WhatsApp",
  push: "Pushbericht",
};

/**
 * Waarom een bericht niet weg is, in gewone taal (ADR-065: geen databasetaal op het
 * scherm). De sleutels zijn de `fout_reden`-strings uit `notificaties_log` — níet de
 * API-error_codes. Onbekende redenen gaan door `noemNetjes()`.
 *
 * Eén woordenboek, hergebruikt op de deelnemerpagina (blok 3, §3.5) en op de
 * berichtenpagina (blok 4, §4.10).
 */
export const BERICHT_REDEN_NAMEN = {
  niet_toegestaan_in_pilot: "Klaargezet — tijdens de pilot gaat er geen mail de deur uit",
  "geen e-mailadres bekend": "Geen e-mailadres bekend",
};

/**
 * De velden van de deelnemer, zoals hij ze op zijn eigen scherm leest.
 *
 * De server geeft bij een onvolledig profiel de kále kolomnamen terug
 * (`huisnummer_toevoeging`, `avg_toestemming_op`). Die horen niet op het scherm: dat is
 * databasetaal, en ADR-065 verbiedt dat. Hier krijgen ze hun gewone woorden.
 *
 * "Uw" ervoor, want dit staat op het scherm van de deelnemer zelf, in een zin als "Er
 * ontbreekt nog: uw telefoonnummer en uw postcode."
 *
 * De sleutels komen één op één uit `ALLE_SLEUTELS` in
 * `backend/app/services/gegevens_compleet.py`. Een test leest die module en wordt rood
 * zodra daar een sleutel bijkomt zonder Nederlandse naam.
 */
export const VELD_NAMEN = {
  voorletters: "uw voorletters",
  achternaam: "uw achternaam",
  geboortedatum: "uw geboortedatum",
  email: "uw e-mailadres",
  telefoon: "uw telefoonnummer",
  straat: "uw straatnaam",
  huisnummer: "uw huisnummer",
  huisnummer_toevoeging: "de toevoeging bij uw huisnummer",
  postcode: "uw postcode",
  plaats: "uw woonplaats",
  avg_toestemming_op: "uw toestemming voor het verwerken van uw gegevens",
};

/** Eén ontbrekend veld in gewone woorden; onbekend valt terug op het `noemNetjes`-vangnet. */
export function veldNaam(sleutel) {
  return noemNetjes(sleutel, VELD_NAMEN);
}

/**
 * De ontbrekende velden als één leesbare opsomming: "a, b en c".
 *
 * Zonder deze functie zou elk scherm zijn eigen `join(", ")` doen en eindigt de zin op
 * "uw postcode, uw woonplaats" in plaats van "uw postcode en uw woonplaats".
 */
export function ontbrekendeVeldenZin(sleutels) {
  const namen = (sleutels || []).map(veldNaam).filter(Boolean);
  if (namen.length === 0) return "";
  if (namen.length === 1) return namen[0];
  return `${namen.slice(0, -1).join(", ")} en ${namen[namen.length - 1]}`;
}

/**
 * Maak van een kale waarde leesbare tekst.
 *
 * Staat de waarde in het woordenboek, dan wint die naam. Zo niet, dan wordt de waarde
 * alsnog leesbaar gemaakt: liggende streepjes worden spaties en de eerste letter een
 * hoofdletter. Dat is het vangnet — een nieuwe waarde ziet er dan hooguit onhandig uit,
 * maar nooit als databasetaal.
 */
export function noemNetjes(waarde, woordenboek = {}) {
  if (waarde === null || waarde === undefined || waarde === "") return "—";
  const sleutel = String(waarde);
  if (woordenboek[sleutel]) return woordenboek[sleutel];
  const leesbaar = sleutel.replace(/[_-]+/g, " ").trim();
  if (!leesbaar) return "—";
  return leesbaar.charAt(0).toUpperCase() + leesbaar.slice(1);
}

/* ── De aanmeldmail: van stand naar scherm (blok 3) ─────────────────────────────
 *
 * Eén bron van waarheid voor de zes standen, zodat de lijstkolom (§3.6) en het
 * detailoverzicht (§3.4) niet uit elkaar lopen. De faalreden komt als kale
 * `fout_reden`-string binnen en gaat via BERICHT_REDEN_NAMEN (met noemNetjes-vangnet).
 */

/** De reden bij `gefaald`/`overgeslagen` in gewone taal (§3.5). */
export function uitnodigingRedenNetjes(foutReden) {
  return noemNetjes(foutReden, BERICHT_REDEN_NAMEN);
}

/** dd-mm uit een ISO-datum, of "" als er niets bruikbaars is. */
function korteDatum(waarde) {
  if (!waarde) return "";
  const d = new Date(waarde);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit" });
}

/**
 * Het korte label voor de kolom "Uitnodiging" in de deelnemerslijst (§3.6), en het
 * detailoverzicht deelt dezelfde zes→vier-collapse. De spec noemt vier weergaven; alle
 * zes standen worden hier expliciet gemapt zodat er nooit een kale stand doorheen glipt.
 */
export function uitnodigingKolomLabel(status, verzondenOp) {
  switch (status) {
    case "pending":
    case "sending":
      return "klaargezet";
    case "verzonden":
    case "geopend": {
      const d = korteDatum(verzondenOp);
      return d ? `✓ ${d}` : "✓ verstuurd";
    }
    case "gefaald":
    case "overgeslagen":
      return "mislukt";
    default:
      // "geen" of onbekend → leeg
      return "—";
  }
}

/* ── De signaalregels ────────────────────────────────────────────────────────
 *
 * Steven, 24-08-2026, kijkend naar het signalenblok: "ik snap hier de ballen van."
 * Er stond `R4.biomechanische_keten`, `pre_traject`, `drempel >= 3`. Dat is de taal van de
 * regel-registry, niet van de podotherapeut.
 *
 * De regels zelf staan in `backend/app/drempelwaarden.py`, met hun onderbouwing in
 * `projecten/fulkra-works/research/07-vragenlijst-onderbouwing.md`. Hieronder staat per regel
 * hoe hij op het scherm heet en wat hij betekent. De test in namen.test.js loopt de
 * registry langs en wordt rood zodra er een regel bijkomt zonder naam.
 */

export const SIGNAAL_NAMEN = {
  "R1.pijn_voet_kritiek": "Ernstige voetpijn",
  "R1.vermoeidheid_kritiek": "Ernstig vermoeid na de werkdag",
  "R1.werkvermogen_kritiek": "Werkvermogen kritiek laag",
  "R1.werkvermogen_aandacht": "Werkvermogen verlaagd",
  "R1.bewegen_aandacht": "Moeite met dagelijks bewegen",
  "R2.werkvermogen_plus_pijn": "Verminderd werkvermogen én pijn",
  "R2.vermoeidheid_plus_bewegen": "Vermoeid én moeite met bewegen",
  "R3.pijn_dagelijks_chronisch": "Elke dag pijn",
  "R3.pijn_wekelijks_subacuut": "Wekelijks pijn, matig tot ernstig",
  "R4.multi_site_klachten": "Klachten op drie of meer plekken",
  "R4.biomechanische_keten": "Voet én knie of heup",
  "R5.pijn_verslechterd": "Pijn is toegenomen na twaalf weken",
  "R5.pijn_klinisch_verbeterd": "Pijn duidelijk afgenomen",
  "R5.werkvermogen_verbeterd": "Werkvermogen vooruitgegaan",
  "R5.bewegen_verbeterd": "Bewegen gaat beter",
  "R5.geen_effect_wk12": "Geen verbetering na twaalf weken",
  "R-aanmeting.pijnplek_aanmeter": "Pijnlijke plek gevonden bij het aanmeten",
};

/**
 * Wat het signaal betekent, in gewone taal, plus waar de grens vandaan komt.
 *
 * Twee zinnen per regel: eerst wat de deelnemer heeft ingevuld waardoor dit signaal ontstond,
 * dan waarom die grens daar ligt. De literatuurverwijzing staat erbij waar die er is — dat is
 * het antwoord op "waar is dit op gebaseerd".
 */
export const SIGNAAL_UITLEG = {
  "R1.pijn_voet_kritiek":
    "De deelnemer gaf zijn voetpijn een 7 of hoger op een schaal van 0 tot 10. "
    + "Vanaf 7 heet pijn in onderzoek \u201eernstig\u201d; dat is de grens waarboven een "
    + "podotherapeut ernaar hoort te kijken.",
  "R1.vermoeidheid_kritiek":
    "De deelnemer gaf een 8 of hoger voor vermoeidheid aan het einde van de werkdag. "
    + "Vanaf 8 is de vermoeidheid zo hoog dat hij het werk zelf raakt.",
  "R1.werkvermogen_kritiek":
    "De deelnemer beoordeelde zijn eigen werkvermogen met een 4 of lager op een schaal van "
    + "0 tot 10 (Work Ability Score). Dat is de grens die in arbeidsonderzoek als kritiek geldt.",
  "R1.werkvermogen_aandacht":
    "Het werkvermogen staat op 6 of lager. Dit is geen alarm maar een let-op: het telt wel mee "
    + "in het overzicht, en samen met pijn wordt het wél een signaal.",
  "R1.bewegen_aandacht":
    "De deelnemer geeft aan gemiddeld meer dan lichte moeite te hebben met alledaagse "
    + "bewegingen zoals traplopen, wandelen en boodschappen doen (PROMIS-PF).",
  "R2.werkvermogen_plus_pijn":
    "Twee dingen tegelijk: het werkvermogen staat op 5 of lager én de voetpijn op 5 of hoger. "
    + "Die combinatie voorspelt uitval beter dan elk van de twee apart, en vraagt om actie van "
    + "de podotherapeut.",
  "R2.vermoeidheid_plus_bewegen":
    "Vermoeidheid van 7 of hoger, samen met moeite met dagelijks bewegen. Die combinatie wijst "
    + "op meer dan alleen een voetprobleem.",
  "R3.pijn_dagelijks_chronisch":
    "De deelnemer heeft elke dag pijn. Dat telt als chronisch, los van hoe erg de pijn op dat "
    + "moment is \u2014 dagelijkse pijn gedraagt zich anders dan pijn die af en toe opkomt.",
  "R3.pijn_wekelijks_subacuut":
    "Wekelijks pijn, en op de schaal een 5 of hoger. Nog niet dagelijks, maar wel op weg "
    + "daarheen.",
  "R4.multi_site_klachten":
    "De deelnemer vinkte drie of meer lichaamsdelen aan met klachten. Menz (2016) laat zien dat "
    + "deze groep ruim drie keer zoveel kans heeft op verzuim binnen een jaar, en minder goed "
    + "reageert op alleen zolen: fysio of ergonomie erbij is dan zinvol.",
  "R4.biomechanische_keten":
    "Voetpijn van 5 of hoger én klachten aan knie of heup. Dat wijst op een keten: wat in de "
    + "voet misgaat, trekt hogerop iets scheef. Ongeveer 40% van de mensen met chronische "
    + "voetpijn krijgt binnen anderhalf jaar rugklachten.",
  "R5.pijn_verslechterd":
    "De pijn is na twaalf weken zolen hoger dan bij de nulmeting. Dit hoort niet te gebeuren en "
    + "vraagt om een controle.",
  "R5.pijn_klinisch_verbeterd":
    "De pijn is met minstens 3 punten gedaald en staat nu op 4 of lager. Drie punten is de "
    + "grens waarboven een patiënt het verschil zelf merkt.",
  "R5.werkvermogen_verbeterd":
    "Het werkvermogen is met minstens 1 punt gestegen ten opzichte van de nulmeting \u2014 de "
    + "kleinste stap die merkbaar is.",
  "R5.bewegen_verbeterd":
    "Dagelijkse bewegingen gaan meetbaar makkelijker dan bij de nulmeting.",
  "R5.geen_effect_wk12":
    "Op minstens twee van de drie gemeten gebieden (pijn, werkvermogen, bewegen) is er na twaalf "
    + "weken geen verbetering. Reden om het traject door te nemen.",
  "R-aanmeting.pijnplek_aanmeter":
    "De aanmeter noteerde tijdens het aanmeten zelf een pijnlijke plek met een 7 of hoger. Dit "
    + "komt dus niet uit een vragenlijst maar uit de praktijk.",
};

/** Wanneer in het traject het signaal ontstond. */
export const FASE_NAMEN = {
  pre_traject: "Nulmeting, vóór de zolen",
  post_start: "Na levering van de zolen",
  verbetering: "Vergelijking met de nulmeting",
};

/**
 * Knipt een uitlegtekst op in blokjes van twee zinnen, met een lege regel ertussen.
 *
 * Steven, 24-08-2026: "wil je na twee zinnen in dit stuk tekst een enter zetten? Dat leest
 * wat makkelijker." Een blok van vier zinnen achter elkaar lees je op een scherm nu eenmaal
 * slechter dan twee keer twee.
 *
 * Er verandert geen woord — er komt alleen witruimte bij. Het scherm dat dit toont, moet wel
 * `whitespace-pre-line` dragen, anders vouwt HTML de regelovergangen weer samen tot één
 * spatie en zie je er niets van.
 */
export function perTweeZinnen(tekst, zinnenPerAlinea = 2) {
  if (typeof tekst !== "string") return "";
  const schoon = tekst.trim();
  if (!schoon) return "";

  // Zinseinde: punt, vraagteken of uitroepteken, gevolgd door witruimte en een hoofdletter.
  // Splitsen gebeurt via een merkteken en niet via spaties — op spaties splitsen zou elk
  // woord een "zin" maken.
  const MERK = "\uE000";
  const zinnen = schoon.replace(/([.!?])\s+(?=[A-ZÀ-ÖØ-Þ(])/g, `$1${MERK}`).split(MERK);

  const blokken = [];
  for (let i = 0; i < zinnen.length; i += zinnenPerAlinea) {
    blokken.push(zinnen.slice(i, i + zinnenPerAlinea).join(" ").trim());
  }
  return blokken.filter(Boolean).join("\n\n");
}

/* ── Hoe hard vraagt een signaal om aandacht? ────────────────────────────────
 *
 * Steven, 24-08-2026: "ik zit te denken om de achtergrond van zo'n rij een hele lichte tint
 * te geven, geel oranje en rood... juist op voorhand, omdat je moet weten wie je extra in de
 * gaten moet houden."
 *
 * De rangorde is niet nieuw bedacht: hij staat al in `backend/app/drempelwaarden.py`, in de
 * velden `actie` en `notificatie`, akkoord van 01-06-2026.
 *
 *   escalatie of directe melding  → hoog    (nu nakijken)
 *   flag, gaat mee in de verzamelmail → middel (in de gaten houden)
 *   aandacht, geen melding        → laag    (telt mee in het beeld)
 *   marker                        → geen tint
 *
 * Die laatste zijn goed nieuws — "Pijn duidelijk afgenomen" hoort niet in een waarschuwkleur.
 *
 * De test in namen.test.js leest de registry en vergelijkt hem regel voor regel met deze
 * lijst, zodat de twee niet uit elkaar kunnen lopen.
 */

export const SIGNAAL_NIVEAUS = {
  "R1.pijn_voet_kritiek": "middel",
  "R1.vermoeidheid_kritiek": "middel",
  "R1.werkvermogen_kritiek": "middel",
  "R1.werkvermogen_aandacht": "laag",
  "R1.bewegen_aandacht": "laag",
  "R2.werkvermogen_plus_pijn": "hoog",
  "R2.vermoeidheid_plus_bewegen": "middel",
  "R3.pijn_dagelijks_chronisch": "middel",
  "R3.pijn_wekelijks_subacuut": "middel",
  "R4.multi_site_klachten": "middel",
  "R4.biomechanische_keten": "middel",
  "R5.pijn_verslechterd": "hoog",
  "R5.pijn_klinisch_verbeterd": "geen",
  "R5.werkvermogen_verbeterd": "geen",
  "R5.bewegen_verbeterd": "geen",
  "R5.geen_effect_wk12": "hoog",
  "R-aanmeting.pijnplek_aanmeter": "middel",
};

/** Van laag naar hoog, zodat je twee niveaus kunt vergelijken. */
export const NIVEAU_VOLGORDE = ["geen", "laag", "middel", "hoog"];

/**
 * Het niveau van één signaal.
 *
 * Begint bij wat de regel zelf zegt, en gaat omhoog als het gemeten pijncijfer daarom vraagt.
 * De banden voor dat cijfer volgen de NRS-11: 1-3 licht, 4-6 matig, 7-10 ernstig. Die grens
 * bij 7 is dezelfde die in de onderbouwing "ernstige pijn" heet en waarop een pijnplek een
 * signaal wordt.
 */
export function signaalNiveau(flag) {
  if (!flag) return "geen";
  const uitDeRegel = SIGNAAL_NIVEAUS[flag.regel_id] || "middel";

  const pijn = Number(flag.pijncijfer);
  let uitHetCijfer = "geen";
  if (Number.isFinite(pijn) && pijn > 0) {
    if (pijn >= 7) uitHetCijfer = "hoog";
    else if (pijn >= 4) uitHetCijfer = "middel";
    else uitHetCijfer = "laag";
  }

  return NIVEAU_VOLGORDE.indexOf(uitHetCijfer) > NIVEAU_VOLGORDE.indexOf(uitDeRegel)
    ? uitHetCijfer
    : uitDeRegel;
}

/** Het zwaarste niveau uit een reeks signalen; voor een samenvattende regel. */
export function hoogsteNiveau(flags) {
  return (flags || []).reduce((hoogste, f) => {
    const n = signaalNiveau(f);
    return NIVEAU_VOLGORDE.indexOf(n) > NIVEAU_VOLGORDE.indexOf(hoogste) ? n : hoogste;
  }, "geen");
}

/** Wat een kleur betekent, voor wie het vraagt. */
export const NIVEAU_UITLEG = {
  hoog: "Nu nakijken",
  middel: "In de gaten houden",
  laag: "Telt mee in het beeld",
  geen: "Goed nieuws",
};

/* ────────────────────────────────────────────────────────────────────────────
 * Het signaal als één zin
 *
 * Steven, 25-08-2026, kijkend naar het signalenblok: "ik vind dat de pijn signalen hier
 * niet heel duidelijk in een paar woorden zeggen wat er precies is." Er stond
 * "Voet-keten verdacht: 3+ lichaamsdelen klachten (Menz 2016)" met daaronder
 * "gemeten 5 · vanaf 3".
 *
 * Vorm van elke zin: <wat gemeten is, met cijfer>, mogelijk <wat het kan betekenen>.
 * De zeventien zinnen zijn woord voor woord door hem goedgekeurd.
 *
 * Het cijfer komt uit `flag.waarden` — de antwoorden van de vragenlijst waar het signaal
 * uit voortkwam. Dat is nodig omdat samengestelde regels (voet + knie/heup) geen
 * `gemeten_waarde` op het signaal zetten; daar staat alleen de voorwaarde. Ontbreekt een
 * waarde (vragenlijst verwijderd of geanonimiseerd), dan valt de zin terug op de variant
 * zonder cijfer. Er mag nooit "undefined" op het scherm komen.
 * ──────────────────────────────────────────────────────────────────────────── */

const BEWEGEN_VELDEN = [
  "bewegen_huishouden",
  "bewegen_traplopen",
  "bewegen_wandelen",
  "bewegen_boodschappen",
];

/** Een getal uit de antwoorden, of null als het er niet (bruikbaar) is. */
function getal(waarde) {
  if (waarde === null || waarde === undefined || waarde === "") return null;
  const n = Number(waarde);
  return Number.isFinite(n) ? n : null;
}

/** Nederlands schrijft 3,5 en niet 3.5. Hele getallen blijven heel. */
function toonGetal(n) {
  if (n === null) return null;
  return Number.isInteger(n) ? String(n) : String(n).replace(".", ",");
}

/** Het gemiddelde van de vier bewegen-vragen, of null als er niets is ingevuld. */
function bewegenGemiddelde(w) {
  const cijfers = BEWEGEN_VELDEN.map((v) => getal(w[v])).filter((n) => n !== null);
  if (!cijfers.length) return null;
  const gem = cijfers.reduce((a, b) => a + b, 0) / cijfers.length;
  return Math.round(gem * 10) / 10;
}

/** `klachten_elders` komt als lijst binnen, soms als JSON-tekst. */
function klachtenLijst(waarde) {
  if (Array.isArray(waarde)) return waarde;
  if (typeof waarde === "string") {
    try {
      const uit = JSON.parse(waarde);
      return Array.isArray(uit) ? uit : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Alleen de klachten die deze regel bedoelt, netjes achter elkaar. */
function knieOfHeup(waarde) {
  const uit = klachtenLijst(waarde).filter((k) => k === "knie" || k === "heup");
  if (!uit.length) return null;
  return uit.length === 1 ? uit[0] : "knie en heup";
}

/** Aantal lichaamsdelen met klachten; "geen" telt niet mee (zoals de regel het telt). */
function aantalKlachten(waarde) {
  return klachtenLijst(waarde).filter((k) => k && k !== "geen").length || null;
}

/**
 * Zet een cijfer in de zin, of laat het weg als het er niet is. De variant zonder cijfer
 * is geen noodgreep maar een volwaardige zin: bij een verwijderde of geanonimiseerde
 * vragenlijst is dat wat er nog eerlijk te zeggen valt.
 */
function metCijfer(n, met, zonder) {
  const t = toonGetal(n);
  return t === null ? zonder : met(t);
}

/**
 * De zeventien zinnen. Elke functie krijgt de gemeten waarden (`w`) en het signaal zelf
 * (`f`, voor het geval de waarde alleen op het signaal staat, zoals bij de aanmeter).
 *
 * `w.vervolg` bevat dezelfde velden uit de vervolgmeting; die is er alleen bij R5.
 */
export const SIGNAAL_ZIN = {
  "R1.pijn_voet_kritiek": (w, f) =>
    metCijfer(
      getal(w.pijn_voet) ?? getal(f.gemeten_waarde),
      (n) => "Pijn " + n + " in de voet, mogelijk ernstige voetklacht",
      "Ernstige pijn in de voet, mogelijk ernstige voetklacht",
    ),

  "R1.vermoeidheid_kritiek": (w, f) =>
    metCijfer(
      getal(w.vermoeidheid_einde_werkdag) ?? getal(f.gemeten_waarde),
      (n) => "Vermoeidheid " + n + " na de werkdag, mogelijk ernstige oververmoeidheid",
      "Zeer vermoeid na de werkdag, mogelijk ernstige oververmoeidheid",
    ),

  "R1.werkvermogen_kritiek": (w, f) =>
    metCijfer(
      getal(w.werkvermogen) ?? getal(f.gemeten_waarde),
      (n) => "Werkvermogen " + n + " van de 10, mogelijk risico op uitval",
      "Werkvermogen sterk verlaagd, mogelijk risico op uitval",
    ),

  "R1.werkvermogen_aandacht": (w, f) =>
    metCijfer(
      getal(w.werkvermogen) ?? getal(f.gemeten_waarde),
      (n) => "Werkvermogen " + n + " van de 10, let op maar nog geen alarm",
      "Werkvermogen verlaagd, let op maar nog geen alarm",
    ),

  "R1.bewegen_aandacht": (w) =>
    metCijfer(
      bewegenGemiddelde(w),
      (n) => "Moeite met traplopen en wandelen (gemiddeld " + n + "), mogelijk beperkt in het dagelijks leven",
      "Moeite met traplopen en wandelen, mogelijk beperkt in het dagelijks leven",
    ),

  "R2.werkvermogen_plus_pijn": (w) => {
    const wv = toonGetal(getal(w.werkvermogen));
    const pijn = toonGetal(getal(w.pijn_voet));
    if (wv !== null && pijn !== null) {
      return "Werkvermogen " + wv + " én pijn " + pijn + ", mogelijk dreigende uitval";
    }
    return "Verminderd werkvermogen én pijn, mogelijk dreigende uitval";
  },

  "R2.vermoeidheid_plus_bewegen": (w) =>
    metCijfer(
      getal(w.vermoeidheid_einde_werkdag),
      (n) => "Vermoeidheid " + n + " én moeite met bewegen, mogelijk overbelasting van het bewegingsapparaat",
      "Vermoeid én moeite met bewegen, mogelijk overbelasting van het bewegingsapparaat",
    ),

  "R3.pijn_dagelijks_chronisch": () => "Elke dag pijn, mogelijk een chronische klacht",

  "R3.pijn_wekelijks_subacuut": (w) =>
    metCijfer(
      getal(w.pijn_voet),
      (n) => "Wekelijks pijn " + n + ", mogelijk een klacht die chronisch wordt",
      "Wekelijks pijn, mogelijk een klacht die chronisch wordt",
    ),

  "R4.multi_site_klachten": (w, f) =>
    metCijfer(
      aantalKlachten(w.klachten_elders) ?? getal(f.gemeten_waarde),
      (n) => "Klachten op " + n + " plekken in het lichaam, mogelijk meer aan de hand dan de voet alleen",
      "Klachten op meerdere plekken in het lichaam, mogelijk meer aan de hand dan de voet alleen",
    ),

  "R4.biomechanische_keten": (w) => {
    const pijn = toonGetal(getal(w.pijn_voet));
    const plek = knieOfHeup(w.klachten_elders);
    if (pijn !== null && plek) {
      return "Pijn " + pijn + " in de voet én klachten aan de " + plek + ", mogelijk overbelasting van de keten";
    }
    if (plek) {
      return "Pijn in de voet én klachten aan de " + plek + ", mogelijk overbelasting van de keten";
    }
    return "Pijn in de voet én klachten aan knie of heup, mogelijk overbelasting van de keten";
  },

  "R5.pijn_verslechterd": (w) => {
    const van = toonGetal(getal(w.pijn_voet));
    const naar = toonGetal(getal((w.vervolg || {}).pijn_voet));
    if (van !== null && naar !== null) {
      return "Pijn gestegen van " + van + " naar " + naar + " na twaalf weken, mogelijk werkt de zool niet";
    }
    return "Pijn gestegen na twaalf weken, mogelijk werkt de zool niet";
  },

  "R5.geen_effect_wk12": (w, f) =>
    metCijfer(
      getal(f.gemeten_waarde),
      (n) => "Geen verbetering op " + n + " van de 3 punten na twaalf weken, mogelijk werkt het programma niet voor deze deelnemer",
      "Geen verbetering na twaalf weken, mogelijk werkt het programma niet voor deze deelnemer",
    ),

  "R-aanmeting.pijnplek_aanmeter": (w, f) =>
    metCijfer(
      getal(f.pijncijfer) || getal(f.gemeten_waarde),
      (n) => "Pijn " + n + " op een plek die de aanmeter vond, mogelijk ernstige lokale klacht",
      "Pijnlijke plek gevonden bij het aanmeten, mogelijk ernstige lokale klacht",
    ),

  // — De drie hieronder zijn goed nieuws. Ze krijgen geen eigen plek in de werklijst
  //   (zie VERBETERING_BIJ), maar houden wel een eigen zin: die is nodig zodra een
  //   verbetering ergens los getoond wordt, en houdt de lijst van zeventien compleet.
  "R5.pijn_klinisch_verbeterd": (w) => {
    const van = toonGetal(getal(w.pijn_voet));
    const naar = toonGetal(getal((w.vervolg || {}).pijn_voet));
    if (van !== null && naar !== null) {
      return "Pijn gedaald van " + van + " naar " + naar + " na twaalf weken, duidelijke verbetering";
    }
    return "Pijn duidelijk gedaald na twaalf weken, duidelijke verbetering";
  },

  "R5.werkvermogen_verbeterd": (w) => {
    const van = toonGetal(getal(w.werkvermogen));
    const naar = toonGetal(getal((w.vervolg || {}).werkvermogen));
    if (van !== null && naar !== null) {
      return "Werkvermogen gestegen van " + van + " naar " + naar + ", vooruitgang";
    }
    return "Werkvermogen vooruitgegaan, vooruitgang";
  },

  "R5.bewegen_verbeterd": () => "Bewegen gaat merkbaar beter dan bij de start, vooruitgang",
};

/**
 * De zin bij één signaal. Valt terug op de Nederlandse naam, en pas daarna op wat de
 * database zegt — zodat er ook bij een onbekende regel iets leesbaars staat.
 */
export function signaalZin(flag) {
  if (!flag) return "Signaal";
  const maak = SIGNAAL_ZIN[flag.regel_id];
  if (maak) {
    try {
      const zin = maak(flag.waarden || {}, flag);
      if (zin) return zin;
    } catch {
      // Eén kapotte waarde mag nooit de hele lijst omver trekken (ADR-053).
    }
  }
  return SIGNAAL_NAMEN[flag.regel_id] || flag.omschrijving || "Signaal";
}

/**
 * Welke verbetering hoort bij welke signalen.
 *
 * Steven, 25-08-2026: "ik denk dat dit alleen moet als we echt een pijnsignaal hebben
 * gezien wat verbtering nodig heeft om uitval te voorkomen, echter word het dan wel heel
 * druk op dit gebied." Daarom staat een verbetering niet als eigen regel in de werklijst,
 * maar als slotzin onder het signaal dat over hetzelfde onderwerp ging. Is er geen zo'n
 * signaal, dan wordt de verbetering hier niet getoond — die staat in de vergelijking van
 * de vragenlijsten naast elkaar.
 */
export const VERBETERING_BIJ = {
  "R5.pijn_klinisch_verbeterd": [
    "R1.pijn_voet_kritiek",
    "R2.werkvermogen_plus_pijn",
    "R3.pijn_dagelijks_chronisch",
    "R3.pijn_wekelijks_subacuut",
    "R4.biomechanische_keten",
    "R-aanmeting.pijnplek_aanmeter",
  ],
  "R5.werkvermogen_verbeterd": [
    "R1.werkvermogen_kritiek",
    "R1.werkvermogen_aandacht",
    "R2.werkvermogen_plus_pijn",
  ],
  "R5.bewegen_verbeterd": [
    "R1.bewegen_aandacht",
    "R2.vermoeidheid_plus_bewegen",
  ],
};

/** De regels die goed nieuws zijn en dus geen eigen plek in de werklijst krijgen. */
export const VERBETER_REGELS = Object.keys(VERBETERING_BIJ);

/** De slotzin onder een signaal, of null als er geen verbetering bij hoort. */
export function verbeteringSlotzin(verbetering) {
  if (!verbetering) return null;
  const w = verbetering.waarden || {};
  const vervolg = w.vervolg || {};
  const moment = vervolg.meet_moment === "week_2" ? "week 2" : "week 12";
  if (verbetering.regel_id === "R5.pijn_klinisch_verbeterd") {
    const naar = toonGetal(getal(vervolg.pijn_voet));
    return naar === null
      ? "Bij de meting van " + moment + " duidelijk gedaald."
      : "Bij de meting van " + moment + " gedaald naar " + naar + ".";
  }
  if (verbetering.regel_id === "R5.werkvermogen_verbeterd") {
    const naar = toonGetal(getal(vervolg.werkvermogen));
    return naar === null
      ? "Bij de meting van " + moment + " vooruitgegaan."
      : "Bij de meting van " + moment + " gestegen naar " + naar + ".";
  }
  if (verbetering.regel_id === "R5.bewegen_verbeterd") {
    return "Bewegen gaat bij de meting van " + moment + " merkbaar beter.";
  }
  return null;
}

/**
 * Splits de signalen van één deelnemer in wat er in de werklijst hoort, met per signaal
 * de verbetering die erbij hoort.
 *
 * Geeft terug: `{ signalen, verbeteringenZonderSignaal }`. Elk signaal krijgt een extra
 * veld `verbetering_zin` als er een verbetering bij hoort.
 */
export function splitsVerbeteringen(flags) {
  const alle = flags || [];
  const verbeteringen = alle.filter((f) => VERBETERING_BIJ[f.regel_id]);
  const signalen = alle.filter((f) => !VERBETERING_BIJ[f.regel_id]);
  const gebruikt = new Set();

  const uit = signalen.map((f) => {
    const passend = verbeteringen.find(
      (v) => (VERBETERING_BIJ[v.regel_id] || []).includes(f.regel_id),
    );
    if (!passend) return f;
    gebruikt.add(passend.flag_id);
    const zin = verbeteringSlotzin(passend);
    return zin ? { ...f, verbetering_zin: zin } : f;
  });

  return {
    signalen: uit,
    verbeteringenZonderSignaal: verbeteringen.filter((v) => !gebruikt.has(v.flag_id)),
  };
}
